import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { compareAsc, addDays } from "date-fns";

import {
  DAYS_UPLOAD_LATER_BANNER,
  getUploadLaterDocsTitle,
} from "@utils/index";
import { AssetModel } from "@interfaces/index";
import { localStorageService } from "@services/index";
import { useUserStore } from "@stores/index";
import {
  AlertTriangleSvg,
  AlertTriangleUrgentSvg,
  AssetItem,
  UploadSvg,
  Button,
  XSvg,
} from "@components/index";

export const UploadLaterBannerDashboard = ({
  assets,
  startDate,
  endDate,
  className,
}: {
  assets: AssetModel[];
  startDate: string;
  endDate: string;
  className?: string;
}) => {
  const disabledFeatures = useUserStore(
    ({ disabledFeatures }) => disabledFeatures,
  );
  const [isHidden, setIsHidden] = useState(true);
  const [isShownMore, setShownMore] = useState(false);

  useEffect(() => {
    const value = localStorageService.getUploadLaterBannerDate();
    const lastDate = new Date(value || "");
    if (
      !value ||
      compareAsc(new Date(), addDays(lastDate, DAYS_UPLOAD_LATER_BANNER)) > 0
    ) {
      setIsHidden(false);
    }
  }, []);

  const onClose = () => {
    setIsHidden(true);
    localStorageService.setUploadLaterBannerDate();
  };

  const filteredAssets = assets.filter(
    (asset) => asset.uploadLaterAgreement || asset.uploadLaterInvoice,
  );
  const isWarning =
    new Date(endDate).getTime() - new Date().getTime() <=
    14 * 24 * 60 * 60 * 1000; // 14 days in milliseconds
  const isUrgent =
    new Date(endDate).getTime() - new Date().getTime() <=
    10 * 24 * 60 * 60 * 1000; // 10 days in milliseconds

  return (
    !disabledFeatures.includes("Assets") &&
    isWarning &&
    !isHidden &&
    filteredAssets.length > 0 && (
      <div
        className={clsx(
          "my-4 rounded-lg border p-4 md:mb-3 md:mt-5 md:px-4 md:py-3.5",
          isUrgent
            ? "border-red-100 bg-red-150"
            : "border-yellow-40 bg-yellow-200",
          className,
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 font-bold text-black-100 *:size-6 *:shrink-0">
            {isUrgent ? (
              <>
                <AlertTriangleUrgentSvg />
                Tax submission deadline approaching — Upload all pending assets
                documents
              </>
            ) : (
              <>
                <AlertTriangleSvg />
                Assets with pending document uploads
              </>
            )}
          </div>
          <button
            type="button"
            className="ml-10 self-start justify-self-end *:text-black-54"
            onClick={onClose}
          >
            <XSvg />
          </button>
        </div>
        {filteredAssets.length > 1 && (
          <div className="hidden pt-3 text-black-54 md:block">
            {isUrgent
              ? `Upload all pending documents to submit tax period ${startDate} - ${endDate}.`
              : "You have marked some of the assets for later document upload. Complete these uploads to avoid issues with your tax period submission."}
          </div>
        )}
        {filteredAssets.length === 1 && (
          <div className="flex items-end justify-between gap-x-6 pt-4">
            <div className="pt-1 text-black-54 md:block">
              {isUrgent ? (
                <>
                  Upload all pending documents for{" "}
                  <span className="font-bold">{filteredAssets[0].name}</span> to
                  submit tax period {startDate} - {endDate}.
                </>
              ) : (
                <>
                  You have marked{" "}
                  <span className="font-bold">{filteredAssets[0].name}</span>{" "}
                  for later document upload. Complete these uploads to avoid
                  issues with your tax period submission.
                </>
              )}
            </div>
            <Link
              to={`/assets/${filteredAssets[0].uniqueId}`}
              className="hidden shrink-0 items-center gap-2 pb-1 font-bold text-primary-100 md:flex"
            >
              Upload Now <UploadSvg />
            </Link>
          </div>
        )}
        {filteredAssets.length > 1 && (
          <div className="mt-3 w-full md:flex md:flex-wrap md:items-center md:gap-y-4">
            {filteredAssets.map((asset, index) => (
              <Fragment key={asset.uniqueId}>
                <div
                  className={clsx(
                    "mx-3 hidden h-9 w-px bg-magenta-224 md:first:hidden",
                    !isShownMore && index > 3 ? "md:hidden" : "md:block",
                  )}
                />
                <div
                  key={asset.uniqueId}
                  className={clsx(
                    "mb-1 border-b border-b-black-24 pb-1 last:mb-0 last:border-b-0 last:pb-0 md:mb-0 md:border-b-0 md:pb-0",
                    "md:gap-x-6",
                    !isShownMore && index > 2 ? "hidden" : "md:flex",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <div className="*:text-black-54">
                      <AssetItem type={asset.type} justTypeIcon />
                    </div>
                    <div>
                      <div className="max-w-56 truncate text-black-100 md:max-w-28">
                        {asset.name}
                      </div>
                      <div className="text-100 text-black-54">
                        {getUploadLaterDocsTitle(asset)}
                      </div>
                    </div>
                  </div>
                  <Link
                    to={`/assets/${asset.uniqueId}`}
                    className="hidden items-center gap-2 font-bold text-primary-100 md:flex"
                  >
                    Upload Now <UploadSvg />
                  </Link>
                </div>
              </Fragment>
            ))}
            {!isShownMore && filteredAssets.length > 3 && (
              <Button
                title="Show more"
                variant="text"
                className="mx-auto mt-4 block h-9 md:mx-0 md:mt-0"
                onClick={() => setShownMore(true)}
              />
            )}
          </div>
        )}
      </div>
    )
  );
};
